import * as vp from 'vopay';
import {
  Box,
  Card,
  Title,
  Table,
  Toast,
  Tooltip,
  GetColorStatus,
  capitalizeFirstLetterOfWords,
  useUpdateQueryParams,
} from 'vopay';
import { FunctionComponent, useEffect, useState, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import WorkflowSearchForm from './WorkflowSearchForm';
import WorkflowContext, { WorkflowCtx } from './Dialogue/WorkflowContext';
import WorkflowDialogue from './Dialogue/WorkflowDialogue';
import { WorkflowDialogueHandler, DialogueHandlerProps } from './Dialogue/types';
import useFetchWorkflows from '@/hooks/useApi/useFetchWorkflows';
import usePostUpdateWorkflow from '@/hooks/useApi/usePostUpdateWorkflow';
import useFetchTransactionTypes from '@/hooks/useApi/useFetchTransactionTypes';
import { usePermissions } from '@/providers/Permissions.provider';
import { useApprovalWorkflow } from '@/providers/ApprovalWorkflow.provider';
import useFetchUserRoles from '@/hooks/useApi/useFetchUserRoles';
import { Trans, useTranslation } from 'react-i18next';
import { useClientData } from '@/providers/ClientData.provider';
import { UtilButton, Button, TableFiltersContainer, TransactionStatus } from 'UI';

type FC = FunctionComponent;

type QueryParams = {
  paymentRails: string;
  status: string;
};

type AppState = {
  isCreate: boolean;
  hasCreateMultiple: boolean;
  isUpdate: boolean;
  numberCreated: number;
  paymentRailName: string;
};

const WorkflowContainer: FC = () => {
  const history = useHistory();
  const { permissions } = usePermissions();
  const [filteredList, setFilteredList] = useState<vp.Workflow[]>([]);
  const [workflowError, setWorkflowError] = useState('');
  const [workflowName, setWorkflowName] = useState('');
  const [showWorkflowErrorAlert, setShowWorkflowErrorAlert] = useState(false);
  const [showWorkflowActivate, setShowWorkflowActivate] = useState(false);
  const [showWorkflowDeactivate, setShowWorkflowDeactivate] = useState(false);
  const [showWorkflowActivateToast, setShowWorkflowActivateToast] = useState(false);
  const [showWorkflowDeactivateToast, setShowWorkflowDeactivateToast] = useState(false);
  const [workflowsResponse, fetchWorkflows] = useFetchWorkflows({});
  const [transactionTypesResponse, fetchTransactionTypes] = useFetchTransactionTypes({});
  const [isUpdateActive, setIsUpdateActive] = useState(false);
  const [workflowId, setWorkflowId] = useState(0);
  const [updateWorkflowResponse, postUpdateWorkflow, , resetUpdateWorkflow] = usePostUpdateWorkflow({});
  const { response: userRolesResponse, fetchUserRoles } = useFetchUserRoles();
  const location = useLocation<AppState | undefined>();
  const { areEnoughApprovers, getPaymentRail } = useApprovalWorkflow();
  const state = location.state
    ? location.state
    : { isCreate: false, paymentRailName: '', isUpdate: false, numberCreated: '' };
  const { isCreate, paymentRailName, isUpdate, numberCreated } = state;
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { t } = useTranslation('translations', { keyPrefix: 'TransactionApproval.Configuration.Workflows' });
  const {
    response: { Country },
  } = useClientData();

  const queryParams: QueryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      paymentRails: params.get('paymentRails') || '',
      status: params.get('status') || '',
    };
  }, [location.search]);
  const paymentRails = useMemo(() => queryParams.paymentRails || '', [queryParams]);
  const status = useMemo(() => queryParams.status || '', [queryParams]);
  const filtersApplied = useMemo(() => {
    return Boolean(paymentRails || status);
  }, [paymentRails, status]);
  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  useEffect(() => {
    fetchWorkflows({});
    fetchTransactionTypes({
      IsWithdraw: 1,
      CountriesID: Country === 'CA' ? 1 : 2,
      IsTransactionApprovalSupported: 1,
    });
  }, []);
  useEffect(() => {
    fetchWorkflows({});
    fetchUserRoles({
      Role: 'approver',
    });
  }, []);
  useEffect(() => {
    if (updateWorkflowResponse) {
      if (updateWorkflowResponse.Success) {
        if (isUpdateActive) {
          setShowWorkflowActivateToast(true);
        } else {
          setShowWorkflowDeactivateToast(true);
        }
        fetchWorkflows({});
      } else {
        setWorkflowError(updateWorkflowResponse.ErrorMessage);
        setShowWorkflowErrorAlert(true);
      }
      resetUpdateWorkflow();
    }
  }, [updateWorkflowResponse]);

  useEffect(() => {
    if (showWorkflowActivateToast) {
      setShowWorkflowActivateToast(false);
    }
  }, [showWorkflowActivateToast]);
  useEffect(() => {
    if (showWorkflowDeactivateToast) {
      setShowWorkflowDeactivateToast(false);
    }
  }, [showWorkflowDeactivateToast]);
  const workflowContext: WorkflowCtx = {
    workflowError,
    workflowName,
    showWorkflowErrorAlert,
    showWorkflowActivate,
    showWorkflowDeactivate,
    showWorkflowSave: false,
    showWorkflowCreate: false,
    workflowDescription: '',
  };
  const tableProps: vp.TableProps<vp.Workflow> = {
    id: 'scheduled-transactions-list',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'name',
        header: t('Table.name'),
        align: 'left',
        template: ({ data }) => getPaymentRail(data.VIOAccountTransactionTypes_ID, transactionTypesResponse),
      },
      {
        id: 'createdOn',
        header: t('Table.createdOn'),
        align: 'left',
        field: 'DateAdded',
      },
      {
        id: 'status',
        header: t('Table.status'),
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" ai="center">
            <TransactionStatus
              status={GetColorStatus(vp.toBool(data.IsActive) ? 'active' : 'inactive')}
              label={capitalizeFirstLetterOfWords(
                vp.toBool(data.IsActive) ? t('Table.Rows.StatusBadge.active') : t('Table.Rows.StatusBadge.inactive'),
              )}
            />
          </Box>
        ),
      },
      {
        id: 'numberOfApprovers',
        header: t('Table.numberOfApprovers'),
        align: 'left',
        field: 'NumberApprovalsRequired',
      },
      {
        id: 'minimumAmount',
        header: t('Table.minimumAmount'),
        align: 'left',
        template: ({ data }) => `$${vp.formattedCurrency(data.MinAmount.toString())}`,
      },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) => {
          const notEnoughApprovers = !areEnoughApprovers(
            data.MinAmount,
            getPaymentRail(data.VIOAccountTransactionTypes_ID, transactionTypesResponse),
            data.NumberApprovalsRequired,
            userRolesResponse,
          );

          return (
            <>
              {permissions.CreateTransactionApprovalWorkflow && (
                <Box gap="sm" jc="fe" ai="center">
                  {notEnoughApprovers && (
                    <Tooltip
                      icon="info"
                      position="left"
                      type="hover"
                      color="#d22d2d"
                      bgColor="transparent"
                      width="350px"
                      iconColor="error"
                      borderColor="#d22d2d"
                      information={
                        <Box flx="col" jc="fs">
                          <div style={{ textAlign: 'left' }}>
                            <Box flx="col" pb="sm">
                              {t('Table.Rows.EnoughApproversTooltip.description')}
                            </Box>
                            <a
                              href="/transaction-approval/configuration/roles"
                              style={{ color: '#3B339E', textDecoration: 'underline' }}
                            >
                              {t('Table.Rows.EnoughApproversTooltip.linkLabel')}
                            </a>
                          </div>
                        </Box>
                      }
                    />
                  )}
                  <Button
                    id={`workflow-edit-button-${data.ID.toString() || ''}`}
                    size="small"
                    onClick={() => {
                      history.push(`/transaction-approval/configuration/workflows/edit/${data.ID}`);
                    }}
                  >
                    {t('Table.Rows.detailsButton')}
                  </Button>
                  {!vp.toBool(data.IsActive) && (
                    <Button
                      id={`workflow-edit-activate-${data.ID.toString() || ''}`}
                      size="small"
                      onClick={() => {
                        setWorkflowName(
                          getPaymentRail(data.VIOAccountTransactionTypes_ID, transactionTypesResponse)[0],
                        );
                        setWorkflowId(data.ID);
                        setShowWorkflowActivate(true);
                      }}
                    >
                      {t('Table.Rows.activateButton')}
                    </Button>
                  )}
                  {vp.toBool(data.IsActive) && (
                    <Button
                      id={`workflow-edit-deactivate-${data.ID.toString() || ''}`}
                      size="small"
                      onClick={() => {
                        setWorkflowName(
                          getPaymentRail(data.VIOAccountTransactionTypes_ID, transactionTypesResponse)[0],
                        );
                        setWorkflowId(data.ID);
                        setShowWorkflowDeactivate(true);
                      }}
                    >
                      {t('Table.Rows.deactivateButton')}
                    </Button>
                  )}
                </Box>
              )}
            </>
          );
        },
      },
    ],
    rows: filteredList.map((workflow) => ({
      id: String(workflow.ID || ''),
      data: workflow,
    })),
  };

  const handlers = {
    handleWorkflowDialogue: (handlerProps: DialogueHandlerProps<WorkflowDialogueHandler>) => {
      const { type: typeHandler, state: state2, action = 'default' } = handlerProps;

      const dialogueHandlers: WorkflowDialogueHandler = {
        error: {
          confirm: {
            default: () => {
              setShowWorkflowErrorAlert(false);
            },
          },
          cancel: {
            default: () => {
              setShowWorkflowErrorAlert(false);
            },
          },
          close: {
            default: () => {},
          },
        },
        activate: {
          confirm: {
            default: () => {
              // show toast and activate
              setShowWorkflowActivate(false);
              setIsUpdateActive(true);
              postUpdateWorkflow({
                WorkflowID: workflowId,
                IsActive: true,
              });
            },
          },
          cancel: {
            default: () => {
              history.push('/transaction-approval/configuration/roles');
            },
          },
          close: {
            default: () => {
              setShowWorkflowActivate(false);
            },
          },
        },
        deactivate: {
          confirm: {
            default: () => {
              // show toast amd decactove
              setShowWorkflowDeactivate(false);
              setIsUpdateActive(false);
              postUpdateWorkflow({
                WorkflowID: workflowId,
                IsActive: false,
              });
            },
          },
          cancel: {
            default: () => {
              history.push('/transaction-approval/configuration/roles');
            },
          },
          close: {
            default: () => {
              setShowWorkflowDeactivate(false);
            },
          },
        },
        save: {
          confirm: {
            default: () => {},
          },
          cancel: {
            default: () => {},
          },
          close: {
            default: () => {},
          },
        },
        create: {
          confirm: {
            default: () => {},
          },
          cancel: {
            default: () => {},
          },
          close: {
            default: () => {},
          },
        },
      };
      const dialogueState = dialogueHandlers[typeHandler][state2];
      const handler = dialogueState ? dialogueState[action] || dialogueState.default : () => {};

      handler();
    },
  };

  const filterResults = () => {
    setFilteredList(
      Object.values(workflowsResponse?.Workflows || {})
        .map((item) => item)
        .filter((data) => {
          const ObjectName = getPaymentRail(data.VIOAccountTransactionTypes_ID, transactionTypesResponse);
          const ObjectStatus = vp.toBool(data.IsActive) ? 'active' : 'inactive';
          return (
            (paymentRails === '' || paymentRails === 'all' || ObjectName.includes(paymentRails)) &&
            (status === '' || status === 'all' || ObjectStatus === status)
          );
        }),
    );
  };
  useEffect(() => {
    filterResults();
  }, [workflowsResponse?.Workflows, paymentRails, status]);

  return (
    <div>
      <WorkflowContext.Provider value={workflowContext}>
        <div style={{ marginBottom: 16 }}>
          <UtilButton
            iconLeft="chevron_left"
            size="large"
            onClick={() => {
              history.replace('/transaction-approval/configuration');
            }}
          >
            {history.length > 1 ? tGlobal('Buttons.back') : t('backButton')}
          </UtilButton>
        </div>
        <Box flx="col" gapSize="36px">
          <Box jc="sb" ai="center">
            <Title title={t('title')} />

            {permissions.CreateTransactionApprovalWorkflow &&
              transactionTypesResponse?.Success &&
              Object.values(workflowsResponse?.Workflows || {}).length !==
                Object.values(transactionTypesResponse?.TransactionTypes || {}).length && (
                <Button
                  id="create-workflow"
                  colorScheme="primary"
                  onClick={() => {
                    history.push('/transaction-approval/configuration/workflows/add');
                  }}
                >
                  {t('createButton')}
                </Button>
              )}
          </Box>
          <Card
            style={{
              padding: '16px',
            }}
          >
            <Box flx="col" gap="sm">
              <TableFiltersContainer filtersApplied={filtersApplied}>
                <TableFiltersContainer.Trigger />
                <TableFiltersContainer.Content>
                  <WorkflowSearchForm
                    paymentRails={paymentRails}
                    status={status}
                    transactionTypesResponse={transactionTypesResponse}
                    search={(searchParams) => {
                      updateQueryParams({
                        paymentRails: searchParams.paymentRails || '',
                        status: searchParams.status || '',
                      });
                    }}
                  />
                </TableFiltersContainer.Content>
              </TableFiltersContainer>
            </Box>
            <Box mb="sm">
              <Table {...tableProps} />
            </Box>
          </Card>
        </Box>

        <Toast
          label={
            <Trans
              i18nKey="TransactionApproval.Configuration.Workflows.successfullyCreatedToast"
              ns="translations"
              count={Number(numberCreated)}
              components={[<span style={{ fontWeight: 700 }} />]}
              values={{ count: numberCreated }}
            />
          }
          isVisible={isCreate && !isUpdate}
        />
        <Toast
          label={
            <Trans
              i18nKey="TransactionApproval.Configuration.Workflows.successfullyUpdatedToast"
              ns="translations"
              components={[<span style={{ fontWeight: 700 }} />]}
              values={{ paymentRailName }}
            />
          }
          isVisible={isCreate && isUpdate}
        />
        <Toast
          label={
            <Trans
              i18nKey="TransactionApproval.Configuration.Workflows.successfullyActivatedToast"
              ns="translations"
              components={[<span style={{ fontWeight: 700 }} />]}
              values={{ workflowName }}
            />
          }
          isVisible={showWorkflowActivateToast}
        />
        <Toast
          label={
            <Trans
              i18nKey="TransactionApproval.Configuration.Workflows.successfullyDeactivatedToast"
              ns="translations"
              components={[<span style={{ fontWeight: 700 }} />]}
              values={{ workflowName }}
            />
          }
          variant="neutral"
          isVisible={showWorkflowDeactivateToast}
        />
        <WorkflowDialogue handleDialogue={handlers.handleWorkflowDialogue} />
      </WorkflowContext.Provider>
    </div>
  );
};
export default WorkflowContainer;
